import { useEffect, useState, type CSSProperties } from "react";
import { eventBus } from "../lib/eventBus";

interface ActiveMessage {
  title?: string;
  body: string;
}

/**
 * Full-screen overlay for a message pushed from the CMS ("Send message" /
 * reception). Subscribes to the realtime event bus for `show_message` events
 * and auto-dismisses after the event's `durationMs`. Rendered above the active
 * scheme so it works for every template - the scheme-only rewrite dropped the
 * old message display, which is why pushed messages stopped showing.
 */
export default function MessageOverlay() {
  const [msg, setMsg] = useState<ActiveMessage | null>(null);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const off = eventBus.on((evt) => {
      if (evt.type !== "show_message") return;
      setMsg({ title: evt.title, body: evt.body });
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setMsg(null), evt.durationMs);
    });
    return () => {
      off();
      if (timer) clearTimeout(timer);
    };
  }, []);

  if (!msg) return null;

  return (
    <div role="alert" style={scrim}>
      <div style={card}>
        {msg.title && <div style={titleStyle}>{msg.title}</div>}
        <div style={bodyStyle}>{msg.body}</div>
      </div>
    </div>
  );
}

const scrim: CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(0,0,0,0.55)",
  backdropFilter: "blur(4px)",
  padding: 64,
};

const card: CSSProperties = {
  maxWidth: "62%",
  background: "#fff",
  color: "#14151A",
  borderRadius: 24,
  padding: "48px 56px",
  boxShadow: "0 30px 80px -20px rgba(0,0,0,0.6)",
  textAlign: "center",
};

const titleStyle: CSSProperties = {
  fontSize: 24,
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  opacity: 0.5,
  marginBottom: 16,
};

const bodyStyle: CSSProperties = {
  fontSize: 48,
  fontWeight: 600,
  lineHeight: 1.25,
};
